import { useLang } from "../i18n";
import { fmtEur } from "../api";
import { ArrowUpRight, ArrowDownRight, Minus, Lock } from "lucide-react";
import { Link } from "react-router-dom";

const STYLE = {
  LONG: { color: "text-emerald-400", border: "border-emerald-500/40", bg: "bg-emerald-500/10", Icon: ArrowUpRight },
  SHORT: { color: "text-red-400", border: "border-red-500/40", bg: "bg-red-500/10", Icon: ArrowDownRight },
  HOLD: { color: "text-amber-400", border: "border-amber-500/40", bg: "bg-amber-500/10", Icon: Minus },
};

export const SignalCard = ({ signal, realtime, locked }) => {
  const { lang, t } = useLang();
  if (locked)
    return (
      <div data-testid="signal-locked-card" className="border border-white/10 bg-[#111111] p-6 flex flex-col items-start gap-3">
        <span className="text-xs tracking-[0.2em] uppercase text-slate-400">{t("signal_title")}</span>
        <Lock className="w-8 h-8 text-slate-600" />
        <p className="text-sm text-slate-400">{t("signal_locked")}</p>
        <Link to="/pricing" data-testid="signal-unlock-link" className="text-sm font-medium bg-emerald-500 hover:bg-emerald-400 text-black rounded px-4 py-2 transition-colors">{t("unlock")}</Link>
      </div>
    );
  if (!signal) return <div className="border border-white/10 bg-[#111111] p-6 text-slate-500 text-sm">—</div>;
  const s = STYLE[signal.direction] || STYLE.HOLD;
  const Icon = s.Icon;
  return (
    <div data-testid="signal-of-the-day" className={`border ${s.border} ${s.bg} p-6`}>
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-[0.2em] uppercase text-slate-400">
          {realtime ? t("signal_title") : t("signal_delayed")}
        </span>
        <span className="text-xs font-mono text-slate-500">{signal.date}</span>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <Icon className={`w-10 h-10 ${s.color}`} />
        <span data-testid="signal-direction" className={`text-4xl font-heading font-extrabold tracking-tight ${s.color}`}>{signal.direction}</span>
        <span className="ml-auto font-mono text-sm text-slate-400">{t("score")}: {signal.score > 0 ? "+" : ""}{signal.score}</span>
      </div>
      <p data-testid="signal-motivation" className="text-sm text-slate-300 mt-4 leading-relaxed">
        {lang === "it" ? signal.motivation_it : signal.motivation_en}
      </p>
      {signal.instruction && (
        <div data-testid="signal-instruction" className={`mt-4 border px-3 py-2 text-xs font-mono leading-relaxed ${
          signal.position === "LONG" ? "border-emerald-500/40 text-emerald-300"
            : signal.position === "SHORT" ? "border-red-500/40 text-red-300" : "border-white/30 text-white"}`}>
          {t(`instr_${signal.instruction}`)}
          {signal.tp_points ? ` · ${t("tp_note")} ${signal.tp_points} pt` : ""}
        </div>
      )}
      {signal.note && <p className="text-xs text-slate-400 mt-2 italic border-l-2 border-white/20 pl-2">{signal.note}</p>}
      <p className="text-[11px] text-slate-500 mt-4 font-mono">
        {t("signal_published")} · 22:15 CET{signal.edited ? ` · ${t("signal_edited")}` : ""}
      </p>
    </div>
  );
};

export const StatsGrid = ({ stats }) => {
  const { lang, t } = useLang();
  if (!stats) return null;
  const cells = [
    { key: "ytd", label: t("ytd_profit"), value: fmtEur(stats.ytd_eur, lang), sub: `${stats.ytd_pct > 0 ? "+" : ""}${stats.ytd_pct}%`, pos: stats.ytd_eur >= 0 },
    { key: "total", label: t("total_perf"), value: fmtEur(stats.total_eur, lang), sub: null, pos: stats.total_eur >= 0 },
    { key: "winrate", label: t("win_rate"), value: `${stats.win_rate}%`, sub: `${stats.n_trades} trades`, pos: true },
    { key: "dd", label: t("max_dd"), value: fmtEur(-stats.max_drawdown_eur, lang), sub: null, pos: false },
    { key: "openpl", label: t("open_pl"), value: fmtEur(stats.open_pl_eur, lang), sub: null, pos: stats.open_pl_eur >= 0 },
    { key: "peryear", label: t("trades_year"), value: stats.trades_per_year, sub: null, pos: true },
  ];
  return (
    <div>
      <div data-testid="stats-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-white/10 divide-x divide-y divide-white/10 md:divide-y-0">
        {cells.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="p-4 bg-[#111111]">
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{c.label}</p>
            <p className={`font-mono text-lg mt-1 ${c.pos ? "text-emerald-400" : "text-red-400"}`}>{c.value}</p>
            {c.sub && <p className="font-mono text-xs text-slate-500">{c.sub}</p>}
          </div>
        ))}
      </div>
      <p data-testid="capital-note" className="text-[11px] text-slate-500 mt-2 leading-relaxed">{t("capital_note")}</p>
    </div>
  );
};
